import CryptoJS from 'crypto-js';
import { delay, getUuid } from '@/sub-luca-plugin/utils';
import { toastInQa, createQaRecordId, transformToQaRecords } from '@/sub-luca-plugin/utils/qa';
import { handleTokenExample, eventBusExample } from '@/sub-luca-plugin/utils/class-example';
import { update_luca_ask_record, create_luca_ask_record } from '@/sub-luca-plugin/apis/qa';
import { getQaStore } from '@/sub-luca-plugin/store/qa';
import { CancelToken } from '@/sub-luca-plugin/store/requestTask';
import { IQaRecordItem } from '../qa-records';
import { IFunctionCall, IToolCall, TypeReasoningContentState } from '@/sub-luca-plugin/types';
import { IMode } from '@/sub-luca-plugin/components/qa-render/dialogue-tool/index.helper';
import { getChatGPTResultService } from '@/sub-luca-plugin/utils/chat-gbot';
import { createSubjectSearchCardSensors } from '@/sub-luca-plugin/utils/sensors';

// 获取签名
export const getSign = async (params: { botKey: string; timestamp: number; uuid: string }) => {
    // debugger
    const { botKey, timestamp, uuid } = params;
    const textToSign = timestamp + uuid;
    const hash = CryptoJS.HmacSHA256(textToSign, botKey);
    return hash.toString(CryptoJS.enc.Base64);
};

export interface IReadStreamParams {
    isCreateQa: boolean; // 是否为首次提问，创建答疑
    lucaRecordId: string;
    question: string;
    returnSuccess: boolean;
    lucaAskChatId: string;
    botChatIdOfStream: string;
    images?: string[];
    char?: string;
    reasoningContentState?: TypeReasoningContentState; // 推理状态
    reasoningContent?: string; // 推理内容
    answerContent?: string;
    functionCall?: IFunctionCall;
    toolCalls?: IToolCall[];
    bizType?: 1; // 1-不保留用户提问，用于AI模式、非题目资源、且首问传图片的场景;
}

export interface ICreateRecordOptions {
    /**
     * 是否需要接口错误处理
     */
    needErrorHandle?: boolean;
    /**
     * 请求完成的回调
     */
    requestFinish?: (params: { success: boolean; lucaAskChatId: string }) => void;
    /**
     * 取消请求
     */
    cancelToken?: CancelToken;
    /**
     * 执行Id
     */
    excuteId?: string;
    /**
     * 是否需要创建猜你想问
     */
    needCreateGuessYouAsk?: boolean;
}

// 断开达模型请求后
export const disconnectGPTAfter = (params: {
    recordId?: string;
    botChatIdOfStream?: string;
    question?: string;
    reasoningContent?: string;
    answerContent?: string;
}) => {
    const { recordId, botChatIdOfStream, question = '', reasoningContent = '', answerContent = '' } = params || {};
    const { qaConfig } = getQaStore()!;
    const { botId } = qaConfig;
    update_luca_ask_record({
        recordId: recordId!,
        textStop: {
            botId: botId!,
            chatId: botChatIdOfStream!,
            chatLogs: [
                { role: 'user', content: question },
                { role: 'assistant', reasoningContent, content: answerContent },
            ],
        },
    });
};

// 在完成拍题答疑的情况下，如果再次追问，将ocr识别卡片的可编辑状态置为不可编辑
export const disableOcrEditOrReplace = () => {
    const { setQaRecords, qaRecords } = getQaStore()!;
    if (qaRecords.some(item => item.type === 'questionOfOcrSearch')) {
        setQaRecords(oldList =>
            oldList.map(item => {
                if (item.type !== 'questionOfOcrSearch') return item;
                return { ...item, hideReplaceOcrResultBtn: true };
            })
        );
    } else if (qaRecords.some(item => item.type === 'ocr')) {
        setQaRecords(oldList =>
            oldList.map(item => {
                if (item.type !== 'ocr') return item;
                update_luca_ask_record({
                    recordId: item.recordId,
                    ocrContent: { ocrContent: item.content, editState: false },
                });
                return { ...item, hideOcrContentEditBtn: true };
            })
        );
    }
};

interface IHandleStatusOfCreateQaRecordsParams {
    status: number;
    needErrorHandle: boolean;
    message?: string;
}

// 创建聊天记录的报错处理
export const handleErrorOfCreateQaRecords = ({ status, needErrorHandle, message }: IHandleStatusOfCreateQaRecordsParams): any => {
    if (status === 0) return null;
    if (!needErrorHandle) return { success: false, message };
    const { qaConfig, pushQaRecords } = getQaStore()!;
    switch (status) {
        case 10513466: // 提问次数限制
        case 10513468: // 名师追问次数用完
            eventBusExample.publish('WarningModal', { type: 'normal', params: { content: message } });
            return { success: false };
        /* 追问次数已用完 */
        case 10513467:
            return { success: false, noAskAgainNum: true, message };
        /* 转名师没有权益 */
        case 10513310: {
            qaConfig.aiTag === 1
                ? eventBusExample.publish('WarningModal', { type: 'recharge' })
                : eventBusExample.publish('WarningModal', { type: 'normal', params: { content: message } });
            return { success: false };
        }
        /* 名师答疑没有权益 */
        case 10513476:
            pushQaRecords({ recordId: createQaRecordId('respondent'), role: 'respondent', type: 'rechargeTeacherQa' });
            return { success: false, needDeleteQuestion: false };
        /* Ai模式，名师追问无权限 */
        case 10513478:
            pushQaRecords({ recordId: createQaRecordId('respondent'), role: 'respondent', type: 'noPermissionOfProbeByAi' });
            return { success: false, needDeleteQuestion: false };
        /* 非Ai模式，名师追问无权限 */
        case 10513479:
            pushQaRecords({ recordId: createQaRecordId('respondent'), role: 'respondent', type: 'noPermissionOfProbeByNonAi' });
            return { success: false, needDeleteQuestion: false };
        default:
            toastInQa(message || '');
            return { success: false };
    }
};

export const getChatSseHeaders = async (botKey: string) => {
    const uuid = getUuid();
    const timestamp = new Date().getTime();
    const sign = await getSign({ botKey, uuid, timestamp });
    const accessToken = await handleTokenExample.getFronttoken();
    return {
        'Content-Type': 'application/json',
        Authentication: `Basic ${accessToken}`,
        UUID: uuid,
        TIMESTAMP: timestamp,
        SIGN: sign,
    } as any;
};

// 获取创建问题时的视频播放进度时间
export const getVideoTimeOfCreateQuestion = () => {
    const { qaConfig, updateQaConfig } = getQaStore()!;
    const { lucaAskChatId, resourceType, videoTime } = qaConfig || {};
    // 直播或视频下才需要传递videoTime
    if (!resourceType || ![1, 5].includes(resourceType)) return undefined;
    // 创建过问题，传递详情中的videoTime
    if (lucaAskChatId) return videoTime;
    const time = eventBusExample.externalCallbacks.getVideoTime?.();
    time && updateQaConfig({ videoTime: time });
    return time;
};

export interface ISendToGPTOptions {
    mode?: IMode;
}
export interface IGetResponseOfGPTParams {
    content?: string;
    images?: string[];
    isContinue?: boolean;
    role?: 'function';
    lucaAskChatId?: string;
    functionCallResults?: Record<string, string>;
    retryType?: 1 | 2 /* 1-首问重试 2-追问重试 */;
    promise?: Promise<any>;
    bizType?: 1 /* 1-不保留用户提问，用于AI模式、非题目资源、且首问传图片的场景  */;
}

export const getResponseOfGPT = async (
    params: IGetResponseOfGPTParams,
    abortChatSseRequest: React.MutableRefObject<(() => void) | undefined>
) => {
    const {
        lucaAskChatId: oldLucaAskChatId,
        botKey,
        lucaId,
        projectId,
        subjectId,
        twoLevelSubjectId,
        sourceMark,
        clientSource,
        entrySource,
        sceneSource,
        courseId,
        csItemId,
        resourceId,
        resourceType,
        questionTag,
    } = getQaStore()?.qaConfig || {};
    const isCreateQa = !oldLucaAskChatId || params.retryType === 1 ? true : false;
    const question = params.content || '';
    const headers = await getChatSseHeaders(botKey!);
    const gptRes = await getChatGPTResultService({
        headers,
        data: {
            equityVersion: 1,
            lucaAskChatId: oldLucaAskChatId,
            lucaId,
            projectId,
            subjectId,
            twoLevelSubjectId,
            sourceMark,
            clientSource,
            entrySource: entrySource,
            sceneSource,
            courseId,
            csItemId,
            resourceId,
            resourceType,
            videoTime: getVideoTimeOfCreateQuestion(),
            ...params,
        },
        extraData: {
            questionTag,
            isCreateQa,
            question,
            oldLucaAskChatId,
            abortChatSseRequest,
        },
    });
    return gptRes;
};

export const getResponseOfCreateLucaRecord = async (
    lucaAskChatId: string | undefined,
    params: Record<string, any>,
    options?: Partial<{ cancelToken: CancelToken; needErrorHandle: boolean }>
) => {
    try {
        const { qaConfig } = getQaStore()!;
        const { cancelToken, needErrorHandle = false } = options || {};
        const { result, status, message } = await create_luca_ask_record(
            {
                lucaAskChatId,
                equityVersion: 1,
                lucaId: qaConfig.lucaId!,
                sourceMark: qaConfig.sourceMark,
                clientSource: qaConfig.clientSource,
                entrySource: qaConfig.entrySource,
                projectId: qaConfig.projectId,
                subjectId: qaConfig.subjectId,
                twoLevelSubjectId: qaConfig.twoLevelSubjectId,
                sceneSource: qaConfig.sceneSource,
                courseId: qaConfig.courseId,
                csItemId: qaConfig.csItemId,
                resourceId: qaConfig.resourceId,
                resourceType: qaConfig.resourceType,
                videoTime: getVideoTimeOfCreateQuestion(),
                ...params,
            },
            { cancelToken }
        );
        const errorData = handleErrorOfCreateQaRecords({ needErrorHandle, status, message });
        if (errorData) return errorData;
        return transformToQaRecords(result?.list || [], { createRecordType: params.recordType });
    } catch (error) {
        return { success: false, message: error };
    }
};

const createQuestionSearchPromise = (lucaAskChatId: string, params: Record<string, any>, directExcute?: boolean) => {
    const excuteRef: { resolve?: (isCreate?: boolean) => void; reject?: () => void } = {};
    const promise = new Promise(async (resolve, reject) => {
        excuteRef.resolve = (isCreate?: boolean) => {
            if (isCreate) {
                const source = getQaStore()!.addRequestController('axios');
                resolve(
                    new Promise(async (resolve2, reject2) => {
                        const { records, error } = await getResponseOfCreateLucaRecord(lucaAskChatId, params, {
                            cancelToken: source.token,
                        });
                        records?.length ? resolve2(records) : error === 'send_cancel' ? reject2(error) : resolve2([]);
                    })
                );
            } else {
                resolve([]);
            }
        };
        excuteRef.reject = reject;
    });
    if (directExcute) excuteRef.resolve!(true);
    return { promise, excuteRef };
};

export const excuteQuestionSearchProcess = (params: Record<string, any>, options: Record<string, any>) => {
    const { lucaAskChatId, question } = params;
    const { isCreateQa, needCreateSubjectSearch, needRefineProblem, subjectSearchOnOff, mode, excuteId } = options;

    // 问题总结调用方法
    const refineProblemFn = (isCreateQa, excuteRef?: { resolve?: (isCreate?: boolean) => void; reject?: () => void }) => {
        needRefineProblem &&
            eventBusExample.publish('NavgationBar', {
                type: 'refineProblem',
                params: {
                    isCreateQa,
                    lucaAskChatId,
                    content: question,
                    excuteRef,
                },
            });
    };

    // 非主动选择“学科搜索”模式 且 模型提问不触发学科搜索，则只走问题总结逻辑
    if (mode !== 'subject_search' && !needCreateSubjectSearch) {
        refineProblemFn(true);
        return () => Promise.resolve();
    }

    const { promise, excuteRef } = createQuestionSearchPromise(lucaAskChatId, {
        recordType: subjectSearchOnOff ? 65 : 35,
        text: { belongType: 0, content: question },
        helpYouCard: subjectSearchOnOff ? undefined : { saveFlag: false },
        subjectSearch: subjectSearchOnOff ? { saveFlag: false } : undefined,
    });

    mode === 'subject_search' && excuteRef.resolve!(true);

    refineProblemFn(isCreateQa, excuteRef);

    const newPromise = promise
        .then(async (records: any) => {
            const saveRequest = records.length
                ? async () => {
                      records[0]?.sensorsData && createSubjectSearchCardSensors(records[0].sensorsData);
                      const dataSource = (records[0] as any).dataSource;
                      createQuestionSearchPromise(
                          lucaAskChatId,
                          {
                              recordType: subjectSearchOnOff ? 65 : 35,
                              askRecordId: records[0].recordId,
                              text: { belongType: 0, content: question },
                              helpYouCard: subjectSearchOnOff ? undefined : { saveFlag: true, similarProblems: dataSource },
                              subjectSearch: subjectSearchOnOff ? { saveFlag: true, subjectSearchCard: dataSource } : undefined,
                          },
                          true
                      );
                      getQaStore()!.pushQaRecords(records);
                      await delay(800);
                  }
                : null;
            if (['analysising', 'searching'].includes(options!.getCurrentStatus()) && saveRequest) {
                options!.hide();
                await saveRequest();
                getQaStore()!.isContinueExcute(excuteId) &&
                    options!.getCurrentStatus() === undefined &&
                    options!.showLoading('lucaThinking');
                return null;
            }
            return saveRequest;
        })
        .catch(error => {
            console.log('excuteQuestionSearchProcess', error);
            options!.hide();
            return null;
        });
    return async (saveQuestionSearch = true) => {
        if (saveQuestionSearch) {
            options!.showLoading('cardInserting');
            const saveRequest = await newPromise;
            options!.hide();
            await saveRequest?.();
        }
    };
};

export const createGuessYouAskPromise = (lucaAskChatId: string | undefined, params: Record<string, any>) => {
    const promise = new Promise<IQaRecordItem[]>(async (resolve, reject) => {
        const source = getQaStore()!.addRequestController('axios');
        const { records, error } = await getResponseOfCreateLucaRecord(lucaAskChatId, params, { cancelToken: source.token });
        error === 'send_cancel' ? reject(error) : resolve(records || []);
    });
    return { promise };
};

export const excuteGuessYouAskProcess = (params: Record<string, any>, options: Record<string, any>) => {
    const { lucaAskChatId, guessYouAskParams } = params;
    const { needCreate = true } = options;
    if (!needCreate) return () => Promise.resolve();
    const { promise } = createGuessYouAskPromise(
        lucaAskChatId,
        guessYouAskParams || {
            recordType: 50,
            guessYouQuestion: { scene: 2, saveFlag: false },
        }
    );
    return async (isSave = true) => {
        if (!isSave) return;
        try {
            options!.showLoading('cardInserting');
            const records = await promise;
            options!.hide();
            if (records.length) {
                createGuessYouAskPromise(lucaAskChatId, {
                    recordType: 50,
                    guessYouQuestion: { saveFlag: true, relateId: (records[0] as any).relateId },
                });
                getQaStore()!.pushQaRecords(records);
                await delay(500);
            }
        } catch (error) {
            console.log('excuteGuessYouAskProcess', error);
            options!.hide();
        }
    };
};

type TMessageType =
    | 'text'
    | 'function_call'
    | 'tool_calls'
    | 'incomplete_answer'
    | 'retry'
    | 'sensitive_words_replace'
    | 'toast_error'
    | 'stream_error'
    | 'token_fail'
    | 'modal_error'
    | 'no_ask_again_num'
    | 'recharge';

interface IMessageData {
    type: TMessageType;
    /**
     *  模型回复内容
     */
    answerContent?: string;
}

interface IFormatMessage extends IMessageData {
    /**
     * 弱网情况下，流返回的字符串数据会被截断，导致用JSON.parse方式转换为对象时会报错，
     * 需要将导致报错的一段流字符保留下来，和下一段流字符拼接继续后续的流解析
     */
    char?: string;
    /**
     *  模型推理内容
     */
    reasoningContent?: string;
    /**
     *  模型推理内容
     */
    reasoningContentState?: TypeReasoningContentState;
    functionCall?: IFunctionCall;
    toolCalls?: IToolCall[];
    /**
     *  流内容中有结束字段
     */
    charDone?: boolean;
}

interface IOptions {
    preReasoningContentState?: TypeReasoningContentState;
    preReasoningContent?: string;
    preAnswerContent?: string;
    preFunctionCall?: IFunctionCall;
    preToolCalls?: IToolCall[];
}

const statusToErrorInfo = (status: number, message: string): IMessageData => {
    switch (status) {
        /*  敏感词 */
        case 10513302:
        case 66661064:
            return { type: 'toast_error', answerContent: '您的提问涉嫌违规，请修改内容后重新提问' };
        case 66661055:
            return { type: 'toast_error', answerContent: 'Luca正在回复，请稍后发送' };
        /* 双端操作状态不对报错 */
        case 10513472:
            return { type: 'toast_error', answerContent: message };
        /* 每天创建问题数超过限制 */
        case 10513466:
            return { type: 'modal_error', answerContent: message };
        /*  bot记录不存在 / prompt不存在 / luca已停用 / prompt数据被删 / 服务不可用 */
        // case 66661002:
        // case 66661006:
        // case 10513004:
        // case 66661066:
        // case 10513006:
        // case -1:
        //     return { type: 'stream_error', content: '很抱歉，服务好像出了点问题，请点击下方【未解决】按钮，将问题提交给名师回答' };
        case 66661063:
            return { type: 'stream_error', answerContent: message || '很抱歉，这个问题有点难，我暂时无法回答' };
        case 10513469:
            return {
                type: 'stream_error',
                answerContent:
                    message ||
                    '我暂时无法回答关于图片的问题，请用文字描述，可快速获得解答。或者点击下方【未解决】按钮，将问题提交给名师回答，但需等待一段时间哦~',
            };
        case 10513467:
            return {
                type: 'no_ask_again_num',
                answerContent: message || '你的追问次数已用完，若还未解决你的疑问，请点击下方【未解决】按钮，将问题提交给名师回答',
            };
        case 553649434:
        case 553649443:
        case 553649437:
            return { type: 'token_fail' };
        /*  没有次数，需要充值  */
        case 10513475:
            return { type: 'recharge' };
        default:
            /*  重试 */
            return { type: 'retry' };
    }
};

// 消息流处理
export const transformCharToMessage = (
    message: string,
    { preReasoningContentState, preReasoningContent, preAnswerContent, preToolCalls, preFunctionCall }: IOptions
): IFormatMessage => {
    let returnMsg: IFormatMessage = {
        type: 'text',
        reasoningContentState: preReasoningContentState,
        reasoningContent: preReasoningContent,
        answerContent: preAnswerContent,
        functionCall: preFunctionCall,
        toolCalls: preToolCalls,
    };
    let char = message;
    try {
        const splitMessage: string[] = message.split('\n\n');
        for (let i = 0; i < splitMessage.length; i++) {
            if (splitMessage[i] === '') continue;
            if (i !== 0) char = splitMessage[i];
            const jsonStrMessage = splitMessage[i].replace(/^data:/g, '');
            const jsonMessage = JSON.parse(jsonStrMessage);
            const { status = 0, message, error, bizCode, choices = [], answer = '', reasoningContent = '' } = jsonMessage || {};
            if (status !== 0) return statusToErrorInfo(Number(status), message);
            /* gpt报错 */
            if (error) return { type: 'retry' };
            switch (bizCode) {
                /*  token超长 */
                case '196':
                    return {
                        type: 'stream_error',
                        answerContent: message || '很抱歉，您发送的信息内容过长，请创建新对话或精简后重新发送。',
                    };
                /*  敏感词 */
                case '197':
                    return { type: 'sensitive_words_replace', answerContent: answer, reasoningContent };
                default:
                    break;
            }
            const { finish_reason, delta = {} } = choices[0] || {};
            const { reasoning_content, content, function_call, tool_calls } = delta;
            if (reasoning_content) {
                returnMsg = {
                    ...returnMsg,
                    reasoningContentState: 0,
                    reasoningContent: (returnMsg.reasoningContent || '') + reasoning_content,
                };
            } else if (function_call) {
                returnMsg = {
                    ...returnMsg,
                    type: 'function_call',
                    functionCall: {
                        name: function_call.name || returnMsg.functionCall?.name,
                        args: (returnMsg.functionCall?.args || '') + function_call.arguments,
                    },
                };
            } else if (tool_calls?.length) {
                returnMsg = {
                    ...returnMsg,
                    type: 'tool_calls',
                    toolCalls: tool_calls.reduce((total: any, item: any) => {
                        const { function: fn = {}, id, index } = item || {};
                        const { name, arguments: args } = fn || {};
                        if (id) {
                            total[index] = { name, id, args: args };
                        } else {
                            total[index].args += args;
                        }
                        return total;
                    }, returnMsg.toolCalls || []),
                };
            } else if (content !== undefined) {
                returnMsg = {
                    ...returnMsg,
                    answerContent: (returnMsg.answerContent || '') + content,
                    reasoningContentState: returnMsg.reasoningContentState === 0 ? 2 : returnMsg.reasoningContentState,
                };
            }
            switch (finish_reason) {
                /*  未回答完整 */
                case 'length':
                    return { ...returnMsg, type: 'incomplete_answer', charDone: true };
                /*  返回functio_call数据 */
                case 'function_call': {
                    const newFunctionCall = {
                        name: returnMsg.functionCall!.name,
                        args: JSON.parse(returnMsg.functionCall!.args as string),
                    };
                    return { type: 'function_call', functionCall: newFunctionCall, charDone: true };
                }
                case 'tool_calls':
                    return {
                        type: 'tool_calls',
                        toolCalls: (returnMsg.toolCalls || []).map(item => ({
                            ...item,
                            args: JSON.parse(item.args as string),
                        })),
                        charDone: true,
                    };
                case 'stop':
                    return { ...returnMsg, charDone: true };
                default:
                    break;
            }
        }
        return returnMsg;
    } catch (e) {
        returnMsg.char = char;
        return returnMsg;
    }
};
